import { HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { Observable, delay, of } from 'rxjs';
import { PortfolioCronogramaService } from '../../features/portfolio/services/portfolio-cronograma.service';
import type { Portfolio } from '../../features/portfolio/services/portfolio-api.service';
import { mockStore, novoId, page } from './mock-data';
import { mockRelatorioBySlug, mockRelatoriosIndicadores } from './mock-relatorios';

type Dict = Record<string, unknown>;

function resposta(body: unknown, status = 200): Observable<HttpResponse<unknown>> {
  // pequeno atraso para simular latência de rede
  return of(new HttpResponse({ status, body })).pipe(delay(250));
}

function buscar(colecao: Dict[], id: string): Dict | undefined {
  return colecao.find((item) => item['id'] === id);
}

function queryParam(url: string, key: string): string | undefined {
  const idx = url.indexOf('?');
  if (idx === -1) return undefined;
  return new URLSearchParams(url.slice(idx + 1)).get(key) ?? undefined;
}

const cronogramaMockService = new PortfolioCronogramaService();

function marcosCronogramaMock(portfolio: Dict): Dict[] {
  const p = portfolio as unknown as Portfolio;
  return cronogramaMockService.sugerirMarcosInstitucionais(p).map((m) => ({
    id: m.id,
    rotulo: m.rotulo,
    dataSugerida: m.data,
    dataEfetiva: undefined,
    dataCalendario: m.data,
    artigo: m.artigo,
    fundamentacao: m.fundamentacao,
    natureza: m.natureza,
    faseWorkflow: m.faseWorkflow,
    desvioPrazo: false,
    atualizadoEm: new Date().toISOString(),
  }));
}

/**
 * Interceptor de API simulada. Ativado apenas no modo mock (ver app.config.ts). Resolve as chamadas
 * /api/v1/* localmente, sem rede/backend, devolvendo os dados de core/mock/mock-data.ts.
 */
export const mockApiInterceptor: HttpInterceptorFn = (request, next) => {
  const path = request.url.split('?')[0];
  const marcador = '/api/v1/';
  const inicio = path.indexOf(marcador);

  if (inicio === -1) {
    return next(request);
  }

  const recurso = path.slice(inicio + marcador.length); // ex.: "demandas/123/historico"
  const partes = recurso.split('/').filter(Boolean);
  const metodo = request.method.toUpperCase();
  const dominio = partes[0];
  const id = partes[1];
  const sub = partes[2];
  const body = (request.body as Dict | null) ?? {};

  switch (dominio) {
    case 'portfolio':
      if (id && partes[2] === 'cronograma' && partes[3] === 'marcos') {
        const marcoCodigo = partes[4];
        if (metodo === 'GET') {
          let marcos = mockStore.cronogramaMarcos[id];
          if (!marcos) {
            const p = buscar(mockStore.portfolios, id);
            marcos = p ? marcosCronogramaMock(p) : [];
            mockStore.cronogramaMarcos[id] = marcos;
          }
          return resposta(marcos);
        }
        if (metodo === 'PATCH' && marcoCodigo) {
          const marcos = mockStore.cronogramaMarcos[id] ?? [];
          const marco = marcos.find((m) => m['id'] === marcoCodigo);
          if (marco && body['dataEfetiva']) {
            marco['dataEfetiva'] = body['dataEfetiva'];
            marco['dataCalendario'] = body['dataEfetiva'];
            marco['desvioPrazo'] =
              String(body['dataEfetiva']) > String(marco['dataSugerida']);
            marco['atualizadoEm'] = new Date().toISOString();
          }
          return resposta(marco ?? {});
        }
      }
      if (id && partes[2] === 'metadados-governanca' && metodo === 'GET') {
        const p = buscar(mockStore.portfolios, id);
        let marcos = mockStore.cronogramaMarcos[id];
        if (!marcos && p) {
          marcos = marcosCronogramaMock(p);
          mockStore.cronogramaMarcos[id] = marcos;
        }
        return resposta({
          portfolioId: id,
          exercicio: p?.['anoExecucao'],
          status: p?.['status'],
          geradoEm: new Date().toISOString(),
          ranking: [],
          alertas: mockStore.alertasPortfolio,
          decisoes: [],
          marcosCronograma: marcos ?? [],
        });
      }
      if (sub === 'alertas') return resposta(mockStore.alertasPortfolio);
      if (sub === 'ranking') return resposta([]);
      if (metodo === 'POST' && !id) {
        const anoRef = body['anoReferenciaConsolidacao'] as number;
        const novo: Dict = {
          id: novoId(),
          anoExecucao: body['anoExecucao'],
          anoReferenciaConsolidacao: anoRef,
          nome: body['nome'],
          status: 'RASCUNHO',
          dataLimiteConsolidacao: `${anoRef}-04-30`,
          criadoEm: new Date().toISOString(),
          atualizadoEm: new Date().toISOString(),
        };
        mockStore.portfolios = [novo, ...mockStore.portfolios];
        return resposta(novo, 201);
      }
      if (id && sub === 'submeter-priorizacao' && metodo === 'POST') {
        const p = buscar(mockStore.portfolios, id);
        if (p) p['status'] = 'EM_PRIORIZACAO';
        return resposta(p ?? {});
      }
      if (id && sub === 'submeter-aprovacao-em' && metodo === 'POST') {
        const p = buscar(mockStore.portfolios, id);
        if (p) p['status'] = 'EM_APROVACAO_EM';
        return resposta(p ?? {});
      }
      if (id && sub === 'aprovar-chefe-em' && metodo === 'POST') {
        const p = buscar(mockStore.portfolios, id);
        if (p) p['status'] = 'APROVADO_CHEFE_EM';
        return resposta(p ?? {});
      }
      if (id && sub === 'publicar-bcg' && metodo === 'POST') {
        const p = buscar(mockStore.portfolios, id);
        if (p) {
          p['status'] = 'PUBLICADO_BCG';
          p['referenciaPublicacaoBcg'] = body['referenciaPublicacaoBcg'];
          p['publicadoEm'] = body['publicadoEm'] ?? new Date().toISOString();
        }
        return resposta(p ?? {});
      }
      if (id && metodo === 'GET') return resposta(buscar(mockStore.portfolios, id) ?? {});
      if (id && metodo === 'PATCH') {
        const p = buscar(mockStore.portfolios, id);
        if (p) {
          if (body['nome'] !== undefined) p['nome'] = body['nome'];
          if (body['observacoesGovernanca'] !== undefined) {
            p['observacoesGovernanca'] = body['observacoesGovernanca'];
          }
        }
        return resposta(p ?? {});
      }
      if (id && metodo === 'POST') {
        return resposta(buscar(mockStore.portfolios, id) ?? {});
      }
      return resposta(page(mockStore.portfolios));

    case 'audit':
      if (partes[1] === 'events') {
        if (metodo === 'GET') {
          let lista = [...mockStore.auditEvents];
          const domain = queryParam(request.url, 'domain');
          const action = queryParam(request.url, 'action');
          const resourceType = queryParam(request.url, 'resourceType');
          if (domain) lista = lista.filter((e) => e['domain'] === domain);
          if (action) lista = lista.filter((e) => e['action'] === action);
          if (resourceType) lista = lista.filter((e) => e['resourceType'] === resourceType);
          lista.sort((a, b) =>
            String(b['occurredAt']).localeCompare(String(a['occurredAt'])),
          );
          return resposta({
            content: lista,
            totalElements: lista.length,
            totalPages: 1,
            page: 0,
            size: lista.length,
          });
        }
        if (metodo === 'POST') {
          const events = (body['events'] as Dict[]) ?? [];
          let accepted = 0;
          let duplicates = 0;
          for (const ev of events) {
            const cid = String(ev['clientEventId'] ?? '');
            if (mockStore.auditEvents.some((e) => e['clientEventId'] === cid)) {
              duplicates++;
              continue;
            }
            mockStore.auditEvents.unshift({
              id: novoId(),
              clientEventId: cid,
              occurredAt: ev['occurredAt'],
              receivedAt: new Date().toISOString(),
              domain: ev['domain'],
              resourceType: ev['resourceType'],
              resourceId: ev['resourceId'],
              action: ev['action'],
              correlationId: ev['correlationId'],
              context: ev['context'],
              actorRef: '***demo',
            });
            accepted++;
          }
          return resposta({ accepted, duplicates, rejected: 0 }, 202);
        }
      }
      return next(request);

    case 'subportfolio':
      if (metodo === 'POST' && !id) {
        const novo: Dict = {
          id: novoId(),
          nome: body['nome'],
          areaTematicaArt7: body['areaTematicaArt7'],
          gestorSubportfolioId: body['gestorSubportfolioId'],
          status: 'ATIVO',
          criadoEm: new Date().toISOString(),
          atualizadoEm: new Date().toISOString(),
        };
        if (body['portfolioId']) {
          novo['portfolioId'] = body['portfolioId'];
        }
        mockStore.subportfolios = [novo, ...mockStore.subportfolios];
        return resposta(novo, 201);
      }
      if (sub === 'composicao') {
        return resposta({
          subportfolioId: id,
          programas: mockStore.programas
            .filter((p) => p['subportfolioId'] === id)
            .map((p) => p['id']),
          projetos: mockStore.projetos
            .filter((p) => p['subportfolioId'] === id)
            .map((p) => p['id']),
          demandas: mockStore.demandas
            .filter((d) => d['subportfolioId'] === id)
            .map((d) => d['id']),
        });
      }
      if (sub === 'alertas') return resposta([]);
      if (sub === 'relatorios-trimestrais') return resposta(metodo === 'POST' ? { id: novoId() } : []);
      if (id && metodo === 'GET') return resposta(buscar(mockStore.subportfolios, id) ?? {});
      if (id && metodo === 'PATCH') {
        const s = buscar(mockStore.subportfolios, id);
        if (s) {
          if (body['nome'] !== undefined) s['nome'] = body['nome'];
          if (body['gestorSubportfolioId'] !== undefined) {
            s['gestorSubportfolioId'] = body['gestorSubportfolioId'];
          }
          if (body['portfolioId'] !== undefined) s['portfolioId'] = body['portfolioId'];
          if (body['desvincularPortfolio'] === true) delete s['portfolioId'];
          if (body['status'] !== undefined) s['status'] = body['status'];
          s['atualizadoEm'] = new Date().toISOString();
        }
        return resposta(s ?? {});
      }
      {
        const portf = queryParam(request.url, 'portfolioId');
        const lista = portf
          ? mockStore.subportfolios.filter((s) => s['portfolioId'] === portf)
          : mockStore.subportfolios;
        return resposta(page(lista));
      }

    case 'programas': {
      if (metodo === 'POST' && !id) {
        const novo: Dict = {
          id: novoId(),
          ...body,
          metas: body['metas'] ?? [],
          indicadores: body['indicadores'] ?? [],
          projetoIds: [],
          status: 'RASCUNHO',
          criadoEm: new Date().toISOString(),
          atualizadoEm: new Date().toISOString(),
        };
        mockStore.programas = [novo, ...mockStore.programas];
        return resposta(novo, 201);
      }
      if (sub === 'indicadores') {
        return resposta((buscar(mockStore.programas, id)?.['indicadores'] as Dict[]) ?? []);
      }
      if (sub === 'acoes-corretivas') {
        return resposta({ id: novoId(), programaId: id, status: 'REGISTRADA' }, 201);
      }
      if (sub === 'instituir-portaria') {
        const prg = buscar(mockStore.programas, id);
        if (prg) {
          prg['status'] = 'INSTITUIDO_PORTARIA';
          prg['portariaInstituicao'] = body['portariaInstituicao'];
        }
        return resposta(prg ?? {});
      }
      if (sub === 'projetos') {
        const prg = buscar(mockStore.programas, id);
        if (prg) {
          prg['projetoIds'] = [...((prg['projetoIds'] as string[]) ?? []), body['projetoId']];
        }
        return resposta(prg ?? {});
      }
      if (sub === 'desvincular-subportfolio') {
        const prg = buscar(mockStore.programas, id);
        if (prg) {
          delete prg['subportfolioId'];
          prg['atualizadoEm'] = new Date().toISOString();
        }
        return resposta(prg ?? {});
      }
      if (sub === 'vincular-subportfolio') {
        const prg = buscar(mockStore.programas, id);
        if (prg) {
          prg['subportfolioId'] = body['subportfolioId'];
          prg['atualizadoEm'] = new Date().toISOString();
        }
        return resposta(prg ?? {});
      }
      if (id && metodo === 'GET') return resposta(buscar(mockStore.programas, id) ?? {});
      const subProg = queryParam(request.url, 'subportfolioId');
      const programas = subProg
        ? mockStore.programas.filter((p) => p['subportfolioId'] === subProg)
        : mockStore.programas;
      return resposta(page(programas));
    }

    case 'projetos': {
      if (metodo === 'POST' && !id) {
        const novo: Dict = {
          id: novoId(),
          faseCiclo: 'DEMANDA',
          ...body,
        };
        mockStore.projetos = [novo, ...mockStore.projetos];
        return resposta(novo, 201);
      }
      if (sub === 'desvincular-portfolio') {
        const prj = buscar(mockStore.projetos, id);
        if (prj) {
          delete prj['portfolioId'];
          delete prj['subportfolioId'];
        }
        return resposta(prj ?? {});
      }
      if (sub === 'vincular-portfolio') {
        const prj = buscar(mockStore.projetos, id);
        if (prj) {
          if (body['portfolioId'] !== undefined) prj['portfolioId'] = body['portfolioId'];
          if (body['subportfolioId'] !== undefined) prj['subportfolioId'] = body['subportfolioId'];
        }
        return resposta(prj ?? {});
      }
      if (sub === 'avancar-fase') {
        const prj = buscar(mockStore.projetos, id);
        if (prj) prj['faseCiclo'] = body['faseDestino'];
        return resposta(prj ?? {});
      }
      if (sub === 'artefatos') {
        return resposta({ id: novoId(), ...body }, 201);
      }
      if (sub === 'workflow') return resposta({ faseAtual: buscar(mockStore.projetos, id)?.['faseCiclo'] ?? 'DEMANDA', gates: [] });
      if (sub === 'alertas') return resposta([]);
      if (id && metodo === 'GET') return resposta(buscar(mockStore.projetos, id) ?? {});
      const subPrj = queryParam(request.url, 'subportfolioId');
      const projetos = subPrj
        ? mockStore.projetos.filter((p) => p['subportfolioId'] === subPrj)
        : mockStore.projetos;
      return resposta(page(projetos));
    }

    case 'demandas': {
      if (metodo === 'POST' && !id) {
        const novo: Dict = {
          id: novoId(),
          fase: 'ELABORACAO',
          ...body,
        };
        mockStore.demandas = [novo, ...mockStore.demandas];
        return resposta(novo, 201);
      }
      if (sub === 'alertas-prazo') return resposta([]);
      if (sub === 'historico') return resposta([]);
      if (id && metodo === 'GET' && !sub) return resposta(buscar(mockStore.demandas, id) ?? {});
      if (id && (metodo === 'POST' || metodo === 'PUT')) {
        const dem = buscar(mockStore.demandas, id);
        if (dem) {
          if (sub === 'desvincular-subportfolio') {
            delete dem['subportfolioId'];
          } else if (sub === 'vincular-subportfolio') {
            dem['subportfolioId'] = body['subportfolioId'];
          } else if (sub === 'enviar-triagem') dem['fase'] = 'TRIAGEM';
          else if (recurso.includes('triagem/concluir')) dem['fase'] = 'ANALISE_EM';
          else if (sub === 'aprovar-em') dem['fase'] = 'APROVACAO_EM';
          else if (sub === 'converter-projeto') {
            const projetoId = (body['projetoId'] as string | undefined) ?? novoId();
            if (!body['projetoId']) {
              const novoProj: Dict = {
                id: projetoId,
                nome: dem['titulo'] ?? 'Projeto convertido',
                natureza: 'ESTRATEGICO',
                tipoInstitucional: 'MELHORIA_GESTAO',
                faseCiclo: 'INICIACAO',
                gerenteProjetoId: 'mock-gestor',
                escopoPreliminar: dem['descricao'],
                subportfolioId: dem['subportfolioId'],
                demandaId: id,
              };
              mockStore.projetos = [novoProj, ...mockStore.projetos];
            }
            dem['fase'] = 'CONVERTIDA_PROJETO';
            dem['projetoId'] = projetoId;
          } else if (sub === 'converter-programa') {
            const programaId = (body['programaId'] as string | undefined) ?? novoId();
            if (!body['programaId']) {
              const novoProg: Dict = {
                id: programaId,
                nome: dem['titulo'] ?? 'Programa convertido',
                objetivoEstrategico: dem['descricao'] ?? 'Programa gerado na conversão.',
                gerenteProgramaId: 'mock-gestor',
                status: 'RASCUNHO',
                subportfolioId: dem['subportfolioId'],
              };
              mockStore.programas = [novoProg, ...mockStore.programas];
            }
            dem['fase'] = 'CONVERTIDA_PROGRAMA';
            dem['programaId'] = programaId;
          }
        }
        return resposta(dem ?? {});
      }
      const subDem = queryParam(request.url, 'subportfolioId');
      const listaDem = subDem
        ? mockStore.demandas.filter((d) => d['subportfolioId'] === subDem)
        : mockStore.demandas;
      return resposta(page(listaDem));
    }

    case 'comissoes': {
      if (partes[1] === 'documentos') {
        const documentoId = partes[2];
        const acao = partes[3];

        if (documentoId && acao === 'versoes' && partes[4] && partes[5] === 'restaurar' && metodo === 'POST') {
          const doc = buscar(mockStore.documentosComissao, documentoId);
          if (!doc) return resposta({}, 404);
          const versoes = mockStore.documentoVersoes[documentoId] ?? [];
          const versaoRestaurar = partes[4];
          const target = versoes.find((v) => v['id'] === versaoRestaurar);
          if (!target) return resposta({}, 404);
          for (const v of versoes) {
            v['atual'] = v['id'] === versaoRestaurar;
          }
          doc['versaoAtual'] = target['versao'];
          doc['atualizadoEm'] = new Date().toISOString();
          return resposta(
            [...versoes].sort(
              (a, b) => Number(b['versao']) - Number(a['versao']),
            ),
          );
        }

        if (documentoId && acao === 'versoes' && !partes[4] && metodo === 'GET') {
          const versoes = mockStore.documentoVersoes[documentoId] ?? [];
          return resposta(
            [...versoes].sort((a, b) => Number(b['versao']) - Number(a['versao'])),
          );
        }

        if (documentoId && !acao && metodo === 'GET') {
          const doc = buscar(mockStore.documentosComissao, documentoId);
          return doc ? resposta(doc) : resposta({}, 404);
        }

        if (!documentoId && metodo === 'GET') {
          return resposta(page([]));
        }
      }
      if (id && sub === 'documentos' && !partes[3] && metodo === 'GET') {
        const comissao = buscar(mockStore.comissoes, id);
        if (!comissao) return resposta({}, 404);
        let lista = mockStore.documentosComissao.filter((d) => d['comissaoId'] === id);
        const categoria = queryParam(request.url, 'categoria');
        if (categoria) {
          lista = lista.filter(
            (d) => d['categoria'] === categoria || d['tipo'] === categoria,
          );
        }
        return resposta(page(lista));
      }
      if (id && sub === 'documentos' && !partes[3] && metodo === 'POST') {
        const com = buscar(mockStore.comissoes, id);
        if (!com) return resposta({}, 404);
        const form = request.body as FormData;
        const tipo = form.get('tipo')?.toString() ?? 'OUTRO';
        const titulo = form.get('titulo')?.toString() ?? 'Documento enviado';
        const nivelSigilo = form.get('nivelSigilo')?.toString() ?? 'OSTENSIVO';
        const arquivo = form.get('arquivo');
        const nomeArquivo =
          arquivo instanceof File ? arquivo.name : typeof arquivo === 'string' ? arquivo : 'upload.pdf';
        const docId = novoId();
        const agora = new Date().toISOString();
        const doc: Dict = {
          id: docId,
          nome: titulo,
          titulo,
          tipo,
          categoria: tipo,
          comissaoId: id,
          comissaoTitulo: com['titulo'],
          comissaoProtocolo: com['protocolo'],
          versaoAtual: 1,
          versao: 1,
          nivelSigilo,
          autor: 'demo',
          criadoEm: agora,
          uploadEm: agora,
          atualizadoEm: agora,
        };
        mockStore.documentosComissao = [doc, ...mockStore.documentosComissao];
        mockStore.documentoVersoes[docId] = [
          {
            id: novoId(),
            documentoId: docId,
            versao: 1,
            nomeArquivo,
            autor: 'demo',
            enviadoPor: 'demo',
            criadoEm: agora,
            enviadoEm: agora,
            atual: true,
          },
        ];
        return resposta(doc, 201);
      }
      if (partes[1] === 'processos') {
        const processoId = partes[2];
        const acao = partes[3];

        if (processoId && acao === 'timeline' && metodo === 'GET') {
          const eventos = mockStore.processoTramitacaoHistorico[processoId] ?? [];
          return resposta([...eventos].sort(
            (a, b) =>
              new Date(String(b['ocorridoEm'])).getTime() - new Date(String(a['ocorridoEm'])).getTime(),
          ));
        }

        if (processoId && acao === 'tramitar' && metodo === 'POST') {
          const proc = buscar(mockStore.processosComissao, processoId);
          if (!proc) return resposta({}, 404);
          const dest = String(body['destinatario'] ?? '');
          const labels: Record<string, string> = {
            GABINETE_EM: 'Gabinete do Estado-Maior',
            CORREGEDORIA: 'Corregedoria',
            CHEM: 'CHEM',
            PM1: 'PM1',
            PM2: 'PM2',
            PM3: 'PM3',
            PM4: 'PM4',
            PM5: 'PM5',
            PM6: 'PM6',
            EPROJ: 'EPROJ',
          };
          const agora = new Date().toISOString();
          const evento: Dict = {
            id: novoId(),
            processoId,
            tipo: 'ENCAMINHAMENTO',
            descricao: `Enviado para ${labels[dest] ?? dest}`,
            destinatario: dest,
            ocorridoEm: agora,
          };
          const hist = mockStore.processoTramitacaoHistorico[processoId] ?? [];
          mockStore.processoTramitacaoHistorico[processoId] = [evento, ...hist];
          proc['atualizadoEm'] = agora;
          proc['status'] = 'EM_ANALISE';
          return resposta(null, 202);
        }

        if (processoId && !acao && metodo === 'GET') {
          const proc = buscar(mockStore.processosComissao, processoId);
          return proc ? resposta(proc) : resposta({}, 404);
        }

        if (!processoId && metodo === 'POST') {
          const comissaoId = body['comissaoId'] as string | undefined;
          if (!comissaoId) {
            return resposta({ title: 'Comissão obrigatória', detail: 'comissaoId é obrigatório.' }, 422);
          }
          const com = buscar(mockStore.comissoes, comissaoId);
          if (!com) return resposta({}, 404);
          if (com['status'] === 'ENCERRADA') {
            return resposta({ title: 'Comissão encerrada', detail: 'Comissão encerrada.' }, 422);
          }
          const agora = new Date().toISOString();
          const procId = novoId();
          const numero = String(100000 + mockStore.processosComissao.length + 1);
          const proc: Dict = {
            id: procId,
            numero,
            comissaoId,
            comissaoTitulo: com['titulo'],
            status: 'EM_ANALISE',
            atualizadoEm: agora,
          };
          mockStore.processosComissao = [proc, ...mockStore.processosComissao];
          const obs = body['observacao'] ? ` — ${body['observacao']}` : '';
          mockStore.processoTramitacaoHistorico[procId] = [
            {
              id: novoId(),
              processoId: procId,
              tipo: 'CRIACAO',
              descricao: `Processo criado por demo${obs}`,
              autorNome: 'demo',
              ocorridoEm: agora,
            },
          ];
          return resposta(proc, 201);
        }

        if (!processoId && metodo === 'GET') {
          let lista = [...mockStore.processosComissao];
          const comissaoId = queryParam(request.url, 'comissaoId');
          const status = queryParam(request.url, 'status');
          const q = queryParam(request.url, 'q');
          if (comissaoId) {
            lista = lista.filter((p) => p['comissaoId'] === comissaoId);
          }
          if (status) {
            lista = lista.filter((p) => p['status'] === status);
          }
          if (q) {
            const termo = q.toLowerCase();
            lista = lista.filter(
              (p) =>
                String(p['numero']).includes(termo) ||
                String(p['comissaoTitulo']).toLowerCase().includes(termo),
            );
          }
          return resposta(page(lista));
        }
      }
      if (partes[1] === 'relatorios' && metodo === 'GET') {
        const filtros = {
          dataInicio: queryParam(request.url, 'dataInicio'),
          dataFim: queryParam(request.url, 'dataFim'),
        };
        if (partes[2]) {
          const body = mockRelatorioBySlug(partes[2], filtros);
          return body ? resposta(body) : resposta({}, 404);
        }
        return resposta(mockRelatoriosIndicadores(filtros));
      }
      if (partes[1] === 'dashboard' && metodo === 'GET') {
        const hoje = new Date();
        hoje.setHours(0, 0, 0, 0);
        const ativas = mockStore.comissoes.filter(
          (c) => c['status'] === 'EM_ANDAMENTO' || c['status'] === 'INSTAURADA',
        );
        const atrasadas = mockStore.comissoes.filter((c) => {
          const prazo = new Date(String(c['prazoConclusao']) + 'T12:00:00');
          prazo.setHours(0, 0, 0, 0);
          const status = c['status'];
          return (
            (status === 'EM_ANDAMENTO' || status === 'INSTAURADA') && prazo.getTime() < hoje.getTime()
          );
        });
        return resposta({
          totalAtivas: ativas.length,
          totalAtrasadas: atrasadas.length,
          totalEncerradasMes: mockStore.comissoes.filter((c) => c['status'] === 'ENCERRADA').length,
          porSecao: [{ secao: 'PM1', pendentes: 1 }],
          alertasCriticos: atrasadas.map((c) => ({
            comissaoId: c['id'],
            protocolo: c['protocolo'],
            diasRestantes: -1,
            criticidade: 'VERMELHO',
          })),
        });
      }
      if (metodo === 'POST' && !id) {
        const ano = new Date().getFullYear();
        const seq = mockStore.comissoes.length + 1;
        const novo: Dict = {
          id: novoId(),
          protocolo: `COM-EM-${ano}-${String(seq).padStart(3, '0')}`,
          status: 'INSTAURADA',
          criadoEm: new Date().toISOString(),
          atualizadoEm: new Date().toISOString(),
          ...body,
        };
        mockStore.comissoes = [novo, ...mockStore.comissoes];
        return resposta(novo, 201);
      }
      if (id && sub === 'membros' && metodo === 'GET') {
        return resposta(mockStore.comissaoMembros[id] ?? []);
      }
      if (id && sub === 'membros' && metodo === 'POST') {
        const com = buscar(mockStore.comissoes, id);
        if (!com) return resposta({}, 404);
        const entrada = (body['membros'] as Dict[]) ?? [];
        const presidentes = entrada.filter((m) => m['funcao'] === 'PRESIDENTE').length;
        if (presidentes !== 1) {
          return resposta(
            {
              title: 'Presidente obrigatório',
              detail: 'Designação exige exatamente um membro com função PRESIDENTE.',
            },
            422,
          );
        }
        const membros = entrada.map((m) => ({
          id: novoId(),
          comissaoId: id,
          usuarioId: m['usuarioId'],
          funcao: m['funcao'],
          designadoEm: new Date().toISOString(),
        }));
        mockStore.comissaoMembros[id] = membros;
        com['status'] = 'EM_ANDAMENTO';
        com['atualizadoEm'] = new Date().toISOString();
        return resposta(membros, 201);
      }
      if (id && !sub && metodo === 'PATCH') {
        const com = buscar(mockStore.comissoes, id);
        if (!com) return resposta({}, 404);
        if (com['status'] === 'ENCERRADA') {
          return resposta(
            { title: 'Comissão encerrada', detail: 'Comissão encerrada não pode ser editada.' },
            409,
          );
        }
        if (body['prazoConclusao']) {
          const prazo = new Date(String(body['prazoConclusao']) + 'T12:00:00');
          const hoje = new Date();
          hoje.setHours(0, 0, 0, 0);
          if (prazo.getTime() <= hoje.getTime()) {
            return resposta({ title: 'Prazo inválido', detail: 'Prazo deve ser posterior à data atual.' }, 422);
          }
        }
        for (const key of ['titulo', 'objetivo', 'escopo', 'tipo', 'prazoConclusao'] as const) {
          if (body[key] !== undefined && body[key] !== null) {
            com[key] = body[key];
          }
        }
        com['atualizadoEm'] = new Date().toISOString();
        return resposta(com);
      }
      if (id && sub === 'encaminhar' && metodo === 'POST') {
        return resposta(null, 202);
      }
      if (id && sub === 'parecer' && metodo === 'POST') {
        return resposta(null, 201);
      }
      if (id && sub === 'encerrar' && metodo === 'POST') {
        const com = buscar(mockStore.comissoes, id);
        if (!com) return resposta({}, 404);
        com['status'] = 'ENCERRADA';
        com['relatorioFinal'] = body['relatorioFinal'];
        if (body['dataEncerramento']) {
          com['dataEncerramento'] = body['dataEncerramento'];
        }
        com['atualizadoEm'] = new Date().toISOString();
        if (body['gerarDemandaMg3p']) {
          const demandaId = novoId();
          com['demandaVinculadaId'] = demandaId;
          mockStore.demandas.unshift({
            id: demandaId,
            titulo: body['tituloDemandaSugerido'] ?? com['titulo'],
            descricao: String(body['relatorioFinal']).slice(0, 500),
            areaTematica: 'TIC',
            fase: 'ELABORACAO',
            comissaoOrigemId: id,
            origemIntegracao: 'COMISSOES_EM',
          });
        }
        return resposta(com);
      }
      if (id && sub === 'gerar-demanda' && metodo === 'POST') {
        const com = buscar(mockStore.comissoes, id);
        if (!com) return resposta({}, 404);
        const demandaId = novoId();
        com['demandaVinculadaId'] = demandaId;
        mockStore.demandas.unshift({
          id: demandaId,
          titulo: body['tituloDemandaSugerido'] ?? com['titulo'],
          descricao: 'Demanda originada de comissão encerrada.',
          areaTematica: 'TIC',
          fase: 'ELABORACAO',
          comissaoOrigemId: id,
          origemIntegracao: 'COMISSOES_EM',
        });
        return resposta(
          {
            comissaoId: id,
            demandaId,
            mensagem: 'Demanda MG3P criada.',
          },
          201,
        );
      }
      if (id && metodo === 'GET') return resposta(buscar(mockStore.comissoes, id) ?? {});
      const status = queryParam(request.url, 'status');
      const lista = status
        ? mockStore.comissoes.filter((c) => c['status'] === status)
        : mockStore.comissoes;
      return resposta(page(lista));
    }

    default:
      // Recurso não mapeado: devolve resposta vazia segura para não quebrar a UI.
      return resposta(metodo === 'GET' ? [] : {});
  }
};
